import { ExternalLink, FileText } from "lucide-react";
import type { PublishedCustomerDocument } from "../../shared/customer-documents";
import { Badge } from "../components/ui/badge";
import { buttonVariants } from "../components/ui/button";
import { DocumentBody } from "./DocumentBody";
import { documentMarkdown } from "./document-markdown";

import { documentStatusVariant, documentMoney, documentDate } from "./customer-document-format";
export function CustomerDocumentReader({
  document,
  imagePrefix,
}: {
  document: PublishedCustomerDocument;
  imagePrefix: string;
}) {
  return (
    <article className="customer-document-reader">
      <header className="space-y-4 border-b pb-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-muted-foreground">
            {document.kind === "knowledge" ? "Knowledge" : document.kind}
          </span>
          {document.kind !== "knowledge" ? (
            <Badge variant={documentStatusVariant(document.status)} className="capitalize">
              {document.status}
            </Badge>
          ) : null}
        </div>
        <h1 className="text-balance text-3xl leading-tight font-bold">{document.title}</h1>
        {document.summary ? (
          <p className="max-w-2xl text-pretty text-base leading-relaxed text-muted-foreground">
            {document.summary}
          </p>
        ) : null}
        <DocumentMetadata document={document} />
      </header>
      {document.documentUrl ? (
        <div className="my-7 flex flex-wrap items-center justify-between gap-4 rounded-sm border bg-muted/30 p-5">
          <div className="flex items-center gap-3">
            <FileText className="size-6 text-link" />
            <div>
              <p className="font-semibold">
                {document.external ? "Source document" : "PDF document"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {document.external
                  ? "Opens in its document provider."
                  : "Open the original document in a new tab."}
              </p>
            </div>
          </div>
          <a
            className={buttonVariants({ variant: "outline" })}
            href={document.documentUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open document <ExternalLink />
          </a>
        </div>
      ) : null}
      {document.blocks ? (
        <div className="py-7">
          <DocumentBody markdown={documentMarkdown(document.blocks)} imagePrefix={imagePrefix} />
        </div>
      ) : null}
    </article>
  );
}

function DocumentMetadata({ document }: { document: PublishedCustomerDocument }) {
  return (
    <>
      {" "}
      {document.kind !== "knowledge" ? (
        <dl className="flex flex-wrap gap-x-8 gap-y-4 text-sm">
          {document.reference ? (
            <div>
              <dt className="text-muted-foreground">Reference</dt>
              <dd className="mt-1 font-medium">{document.reference}</dd>
            </div>
          ) : null}
          {document.amountMinor !== null ? (
            <div>
              <dt className="text-muted-foreground">Quote amount</dt>
              <dd className="mt-1 font-semibold tabular-nums">
                {documentMoney(document.amountMinor)}
              </dd>
            </div>
          ) : null}
          {document.documentDate ? (
            <div>
              <dt className="text-muted-foreground">
                {document.status === "signed" || document.status === "accepted"
                  ? "Signed on"
                  : "Document date"}
              </dt>
              <dd className="mt-1">{documentDate(document.documentDate)}</dd>
            </div>
          ) : null}
          {document.expiresOn ? (
            <div>
              <dt className="text-muted-foreground">Expires</dt>
              <dd className="mt-1">{documentDate(document.expiresOn)}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </>
  );
}
